import React, { useCallback, useContext, useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { flatMap, startCase } from "lodash";
import { OBJECT_TYPE } from "common/constants";
import { formatCollection } from "common/utils/strings";
import { SUBMISSION_CATEGORY_TO_LABEL } from "common/utils/submissions";
import {
  GetSubmissionTypesQuery,
  useGetSubmissionTypesQuery,
} from "../../../../generated/graphql";
import { AuthContext } from "../../../Authorization/AuthContext";
import Table from "../../../Common/Tables/Table";
import { ActionButton } from "./ActionButton";
import { generateLabelsForRoles } from "./SubmissionsBuilderV2/RoleAndGroupSelector";
import {
  VIEWER_TYPE,
  viewerTypeLabel,
} from "./SubmissionsBuilderV2/viewerType";

type SubmissionTypesTableProps = Pick<
  NonNullable<GetSubmissionTypesQuery["account"]>["submissionTypes"][number],
  | "id"
  | "name"
  | "canCreate"
  | "canCreateGroups"
  | "category"
  | "attachments"
  | "isDeletable"
  | "hiddenFromPublicByDefault"
>;

const SubmissionTypesTable = () => {
  const { account } = useContext(AuthContext);
  const {
    data: currentData,
    error,
    loading,
    refetch,
  } = useGetSubmissionTypesQuery({
    variables: { accountId: account!.id },
    // Need to do cache-and-network because refetch queries wasn't working for submission type create
    fetchPolicy: "cache-and-network",
  });

  const formatAttachment = useCallback(
    (
      attachment: SubmissionTypesTableProps["attachments"][number],
    ): string[] => {
      if (attachment.type === OBJECT_TYPE.PROPERTY) {
        return [`${startCase(attachment.type)}`];
      }
      return (
        attachment.customMapIds?.map(
          id => account?.customMaps.find(map => map.id === id)?.name ?? id,
        ) ?? []
      );
    },
    [account?.customMaps],
  );

  const columns: Array<ColumnDef<SubmissionTypesTableProps>> = useMemo(
    () => [
      {
        id: "name",
        header: "Record type",
        accessorKey: "name",
        enableSorting: false,
        minSize: 300,
      },
      {
        id: "category",
        header: "Record category",
        enableSorting: false,
        cell: ({ row }) =>
          SUBMISSION_CATEGORY_TO_LABEL[row.original.category] ?? null,
      },
      {
        id: "attachments",
        header: "Object type",
        enableSorting: false,
        cell: ({ row }) => {
          return flatMap(row.original.attachments, attachment =>
            formatAttachment(attachment),
          ).join(", ");
        },
      },
      {
        id: "canCreate",
        header: "Who can fill out",
        enableSorting: false,
        cell: ({ row }) =>
          formatCollection([
            ...generateLabelsForRoles(row.original.canCreate),
            ...row.original.canCreateGroups.map(group => group.name),
          ]),
      },
      {
        id: "hiddenFromPublicByDefault",
        header: "Who can view",
        enableSorting: false,
        cell: ({ row }) =>
          row.original.hiddenFromPublicByDefault ?
            viewerTypeLabel[VIEWER_TYPE.TEAM]
          : viewerTypeLabel[VIEWER_TYPE.TEAM_AND_GUESTS],
      },
      {
        id: "actions",
        size: 75,
        maxSize: 75,
        enableSorting: false,
        cell: row => (
          <ActionButton
            submissionType={row.row.original}
            onDeleteSuccess={() => void refetch()}
          />
        ),
      },
    ],
    [formatAttachment, refetch],
  );

  if (error) {
    return <div />;
  }

  return (
    <Table<SubmissionTypesTableProps>
      columns={columns}
      currentData={currentData?.account?.submissionTypes ?? []}
      loadingDetails={{
        loading,
        loadingText: "Loading Record Types...",
        noDataText: "No Record Types Found",
      }}
      tableStyleDetails={{
        hasRowActions: true,
        isCompact: true,
        hasFlushedSides: true,
        hasHighlights: true,
      }}
    />
  );
};

export default SubmissionTypesTable;
